import React, { useState, useEffect } from 'react';
import {
  X,
  Eye,
  EyeOff,
  Copy,
  Edit,
  Globe,
  User,
  Folder,
  Users,
  Calendar,
  ExternalLink,
  Check,
} from 'lucide-react';
import { VaultItem, VaultFolder } from '@/models/vault.model';
import { decryptVaultItemPassword } from '@/services/vault.service';

interface VaultItemDetailProps {
  item: VaultItem;
  folders: VaultFolder[];
  onClose: () => void;
  onEdit: () => void;
}

export const VaultItemDetail: React.FC<VaultItemDetailProps> = ({
  item,
  folders,
  onClose,
  onEdit,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const folder = folders.find((f) => f.id === item.folderId);

  const handleRevealPassword = async () => {
    if (showPassword) {
      setShowPassword(false);
      setPassword(null);
      return;
    }

    setLoading(true);
    try {
      const decrypted = await decryptVaultItemPassword(item);
      setPassword(decrypted);
      setShowPassword(true);

      // Auto-hide after 30 seconds
      setTimeout(() => {
        setShowPassword(false);
        setPassword(null);
      }, 30000);
    } catch (error) {
      console.error('Failed to decrypt password:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleCopyPassword = async () => {
    try {
      const decrypted = password || (await decryptVaultItemPassword(item));
      await navigator.clipboard.writeText(decrypted);
      setCopied('password');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      console.error('Failed to copy password:', error);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">{item.title}</h2>
            {item.isTeam && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                <Users className="w-3 h-3" />
                Team
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Edit"
            >
              <Edit className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Username */}
          {item.username && (
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-500 mb-1">Username / Email</div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{item.username}</span>
                  <button
                    onClick={() => handleCopy(item.username!, 'username')}
                    className={`p-1 rounded transition-colors ${
                      copied === 'username'
                        ? 'bg-green-100 text-green-600'
                        : 'hover:bg-gray-100 text-gray-400'
                    }`}
                    title="Copy"
                  >
                    {copied === 'username' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Password */}
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center text-gray-400 mt-0.5">
              •••
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">Password</div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium text-gray-900 bg-gray-100 px-3 py-1 rounded">
                  {loading
                    ? 'Loading...'
                    : showPassword && password
                    ? password
                    : '••••••••••••'}
                </span>
                <button
                  onClick={handleRevealPassword}
                  disabled={loading}
                  className="p-1 hover:bg-gray-100 rounded transition-colors text-gray-400"
                  title={showPassword ? 'Hide' : 'Show'}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={handleCopyPassword}
                  className={`p-1 rounded transition-colors ${
                    copied === 'password'
                      ? 'bg-green-100 text-green-600'
                      : 'hover:bg-gray-100 text-gray-400'
                  }`}
                  title="Copy"
                >
                  {copied === 'password' ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* URL */}
          {item.url && (
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-500 mb-1">Website</div>
                <div className="flex items-center gap-2">
                  <a
                    href={item.url.startsWith('http') ? item.url : `https://${item.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                  >
                    {item.url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <button
                    onClick={() => handleCopy(item.url!, 'url')}
                    className={`p-1 rounded transition-colors ${
                      copied === 'url'
                        ? 'bg-green-100 text-green-600'
                        : 'hover:bg-gray-100 text-gray-400'
                    }`}
                    title="Copy"
                  >
                    {copied === 'url' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Folder */}
          {folder && (
            <div className="flex items-start gap-3">
              {folder.isTeam ? (
                <Users className="w-5 h-5 text-gray-400 mt-0.5" />
              ) : (
                <Folder className="w-5 h-5 text-gray-400 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="text-sm text-gray-500 mb-1">Folder</div>
                <span className="font-medium text-gray-900">{folder.name}</span>
              </div>
            </div>
          )}

          {/* Notes */}
          {item.notes && (
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center text-gray-400 mt-0.5 text-xs">
                📝
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-500 mb-1">Notes</div>
                <p className="text-gray-900 whitespace-pre-wrap">{item.notes}</p>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>Created {formatDate(item.createdAt)}</span>
            </div>
            {item.updatedAt.getTime() !== item.createdAt.getTime() && (
              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                <Calendar className="w-4 h-4" />
                <span>Updated {formatDate(item.updatedAt)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
        </div>
      </div>
    </div>
  );
};
